/**
 * LearnMoreLink — a ghost icon control with Carbon's 16px external icon that
 * opens an external Microsoft Learn (or other doc) URL in a new tab without
 * triggering the surrounding row's primary click target.
 *
 * Used inside PriorityAction rows where the row itself is a `<Link>` to an
 * in-app deep dive: the action's primary CTA stays on the in-app route, and
 * the user can click this icon as a separate affordance to open the doc in
 * a new tab. We render a `<button>` (not a nested `<a>`) because nested
 * anchors are invalid HTML, and use `stopPropagation` + `preventDefault` so
 * the surrounding Link doesn't navigate when the icon is clicked.
 */

import clsx from 'clsx'
import { IconExternal } from '@/components/carbon/icons'

interface LearnMoreLinkProps {
  /** Microsoft Learn (or equivalent) URL. When null/undefined the component
   *  renders nothing — callers can pass `action.learnMoreUrl` directly. */
  url: string | null | undefined
  /** Hit target: 'sm' (24px) suits compact rows (PillarShell Action), 'md'
   *  (32px) the larger PriorityQueue rows. The icon is 16px in both. */
  size?: 'sm' | 'md'
  /** Optional accessible label override. Defaults to "Open Microsoft Learn
   *  reference in a new tab" — adequate for the BPA rows that ship today. */
  ariaLabel?: string
  /** Replaces the ghost styling entirely (the pre-Carbon contract), so a
   *  caller's override never fights the default classes. */
  className?: string
}

export default function LearnMoreLink({
  url,
  size = 'sm',
  ariaLabel = 'Open Microsoft Learn reference in a new tab',
  className,
}: LearnMoreLinkProps) {
  if (!url) return null

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(url, '_blank', 'noopener,noreferrer')
      }}
      aria-label={ariaLabel}
      className={
        className ??
        clsx(
          'inline-grid flex-none place-items-center bg-transparent text-link',
          'transition-colors duration-110 ease-carbon hover:bg-layer-hover hover:text-link-hover',
          size === 'md' ? 'h-8 w-8' : 'h-6 w-6',
        )
      }
    >
      <IconExternal size={16} />
    </button>
  )
}
